import { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { PermissionLevel } from '@cfa/pathway-fe-shared';
import { Plan } from '@cfa/pathway-fe-shared/xp';
import { Dropdown } from '@cfa/react-components';
import { LoadingIndicator } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import {
  useCategoryLabels,
  useCategoryLabelsGrouped,
} from '@/hooks/useCategoryLabels';
import { useFlags } from '@/hooks/useFlags';
import { usePlansShouldShowTabs } from '@/hooks/usePlansShouldShowTabs';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import type { AddSortFields } from '@/hooks/useSortOptions';
import { useSortOptions } from '@/hooks/useSortOptions';

import { useUserPermissionLevel } from '@/utils/auth-check';
import { isApiError } from '@/utils/request';
import { withRoles } from '@/utils/withRoles';

import GenericError from '@/components/Error/GenericError';
import Filter, { FilterDesktop, FilterMobile } from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import { FilterCheckboxSectionProps } from '@/components/Filter/components/FilterCheckboxSection';
import { FilterSearchBarProps } from '@/components/Filter/components/FilterSearchBar';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';

import { plansViewingAtom } from '../state';

import AddPlanFab from './AddPlanFab';
import ManagePlansEmbedVideoBanner from './ManagePlansEmbedVideoBanner';
import AddTemplateModal from './Modals/AddTemplateModal';
import CreateCustomPlanModal from './Modals/CreateCustomPlanModal';
import PlanCards from './PlanCards';

import { useDataView } from '@/atoms/data-view';
import { TranslationKey } from '@/i18n';
import useGetManagePlansData from '@/routes/TrainingPlans/hooks/useGetManagePlansData';

const dataViewKey = 'manage-plans';

const planTypeViewingOptions = [
  {
    id: 'active',
    display: 'TrainingPlans.planTypeViewingOptions.active',
  },
  {
    id: 'archived',
    display: 'TrainingPlans.planTypeViewingOptions.archived',
  },
] as const satisfies { id: string; display: TranslationKey }[];

const ManagePlansTab = () => {
  const { t } = useTranslation();
  const { planDescriptions: planDescriptionsFF } = useFlags();
  const userCanArchiveUnarchivePlans = useUserPermissionLevel(
    PermissionLevel.atLeast.LO,
  );
  const [plansViewing, setPlansViewing] = useAtom(plansViewingAtom);
  const { data: auth } = useAuth();
  const isLicenseeUser = auth?.isLicensee ?? false;
  const [showAddPlanMenu, setShowAddPlanMenu] = useState(false);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const sortOptions = useSortOptions();

  const { plansDataError, isFetchingPlans, refetchPlans } =
    useGetManagePlansData({
      plansStatus: plansViewing === 'active' ? 'ACTIVE' : 'INACTIVE',
    });

  const tabsActive = usePlansShouldShowTabs();

  useScrollMemory({
    container: 'body',
    key: dataViewKey,
    enabled: !isFetchingPlans,
  });

  const [
    {
      display: { items, total },
      filters,
      sort,
    },
    {
      setSort,
      toggleCheckboxFilter,
      setSearch,
      resetAllCheckboxFilters,
      resetSearch,
    },
  ] = useDataView<AddSortFields<Plan>>(dataViewKey);
  const categoriesValues = Object.values(filters.in.category ?? []);

  const categoryLabels = useCategoryLabels(categoriesValues, isLicenseeUser);
  const categoryLabelsGrouped = useCategoryLabelsGrouped(
    categoriesValues,
    isLicenseeUser,
  );

  if (isApiError(plansDataError)) {
    datadogRum.addError(new Error(plansDataError.message), {
      context: JSON.stringify(plansDataError),
    });
    return <GenericError />;
  }

  const categories: FilterCheckboxSectionProps = {
    labels: categoryLabelsGrouped,
    onToggle: value => toggleCheckboxFilter('category', value),
    title: t('Browse.categories'),
  };

  const search: FilterSearchBarProps = {
    onChange: e => {
      setSearch(
        e.target.value,
        planDescriptionsFF
          ? ['nameLocalLang', 'descriptionSearch']
          : 'nameLocalLang',
      );
    },
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.filtering.searchPlans'),
    value: filters.search,
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      total
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    search: <Filter.SearchBar {...search} />,
    children: <Filter.CheckboxSection {...categories} />,
  };

  const translatedPlanTypeOptions = planTypeViewingOptions.map(option => ({
    ...option,
    display: t(option.display),
  }));

  const mobileBeforeSort = userCanArchiveUnarchivePlans ? (
    <Dropdown
      fullWidth
      getOptionId={option => option.id}
      getOptionText={option => option.display}
      label={`${t('Button.view')}:`}
      onChange={option => {
        if (option) {
          setPlansViewing(option.id);
        }
      }}
      options={translatedPlanTypeOptions}
      renderOption={option => option.display}
      value={
        translatedPlanTypeOptions.find(option => option.id === plansViewing) ??
        null
      }
      className="mb-4 text-left"
    />
  ) : undefined;

  const filterOptionsMobile = {
    filterAndSortButtonOptions: {
      ...filterAndSortButtonOptions,
      beforeSort: mobileBeforeSort,
    },
    children: <Filter.CheckboxSection {...categories} />,
    search: <Filter.SearchBar {...search} />,
  };

  return (
    <>
      <div className="relative flex max-w-full grow flex-col">
        <div className="flex w-full flex-col">
          <ManagePlansEmbedVideoBanner />
          <FilterMobile {...filterOptionsMobile} />
          {/* Top Row */}
          <div>
            <div className="flex justify-end md:pl-[304px]">
              <SortFilterHeader
                label={t('TrainingPlans.filtering.sortBy')}
                onChange={setSort}
                onClear={resetAllCheckboxFilters}
                options={sortOptions}
                showClear={false}
                showCompletedPlansOption={false}
                showMyCompletedPlansOnlySwitchValue={false}
                text={`${total} ${t('TrainingPlans.filtering.plans')}`}
                title={t(
                  plansViewing === 'active'
                    ? 'TrainingPlans.planTypeViewingOptions.active'
                    : 'TrainingPlans.planTypeViewingOptions.archived',
                )}
                total={total}
                value={sortOptions.find(
                  option =>
                    option.key === sort?.key &&
                    option.direction === sort?.direction,
                )}
                leftDropdownMenuSettings={
                  userCanArchiveUnarchivePlans
                    ? {
                        label: t('Button.view'),
                        options: translatedPlanTypeOptions,
                        value: planTypeViewingOptions.find(
                          option => option.id === plansViewing,
                        ),
                        onSelectionChange: selectedOption =>
                          setPlansViewing(selectedOption.id),
                        displayKey: 'display',
                      }
                    : undefined
                }
              />
            </div>
            {/* Bottom Row */}
            <div className="mt-2.5 flex justify-between">
              {/* Left Side */}
              <FilterDesktop {...filterOptions} tabsActive={tabsActive} />
              {/* Right Side */}
              <div className="w-full">
                {isFetchingPlans ? (
                  <div className="flex justify-center pt-10">
                    <LoadingIndicator />
                  </div>
                ) : (
                  <PlanCards
                    categoryLabels={categoryLabels}
                    currentFilters={Object.values(filters.in).flat()}
                    filteredAndSortedPlans={items}
                    isFetchingPlans={isFetchingPlans}
                    refetchPlans={refetchPlans}
                    searchFilter={filters.search}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddPlanFab
        showAddPlanMenu={showAddPlanMenu}
        setShowAddPlanMenu={setShowAddPlanMenu}
        onBuildCustomPlan={() => {
          setShowAddPlanMenu(false);
          setShowCreatePlanModal(true);
        }}
        onChooseFromLibrary={() => {
          setShowAddPlanMenu(false);
          setShowAddTemplateModal(true);
        }}
      />
      <AddTemplateModal
        isOpen={showAddTemplateModal}
        setIsOpen={setShowAddTemplateModal}
      />
      <CreateCustomPlanModal
        isOpen={showCreatePlanModal}
        setIsOpen={setShowCreatePlanModal}
      />
    </>
  );
};

const ManagePlansTabWithRoles = withRoles(ManagePlansTab, 'plans.manage');

export default ManagePlansTabWithRoles;
